import { Notifications } from '@/composables/notification';
import { reactive, readonly } from 'vue';

/**
 * Client-side mirror of the backend `ConversationChangeKind` enum. The hub may
 * serialize the value as either a number (default System.Text.Json enum policy)
 * or the string name (when a `JsonStringEnumConverter` is configured), so both
 * forms are normalized here.
 */
export enum ConversationChangeKind {
    ConversationCreated = 1,
    InboundMessageAdded = 2,
    OutboundMessageAdded = 3,
    MessageUpdated = 4,
    MessageRemoved = 5,
}

/**
 * PHI-free real-time change payload pushed over `/ws/conversations`. Mirrors the
 * backend `ConversationChangedDto`; intentionally carries no message body,
 * author, phone numbers, or content. Authoritative data must be re-fetched via
 * the generated OpenAPI client / existing composables.
 */
export interface ConversationChangedDto {
    conversationSid: string;
    userboxId: string;
    kind: ConversationChangeKind | number | string;
    lastActivityUtc: string;
    isUnlinked: boolean;
    originatingUserId?: string | null;
}

export type ConversationChangeListener = (event: NormalizedConversationChange) => void;

/** A `ConversationChangedDto` with `kind` coerced to the numeric enum. */
export interface NormalizedConversationChange extends ConversationChangedDto {
    kind: ConversationChangeKind;
}

// Bounded dedupe cache: oldest keys are evicted once the cap is reached so a
// long-lived session cannot leak memory from a growing key set.
const DEDUPE_MAX_ENTRIES = 500;
const DEDUPE_TTL_MS = 5 * 60 * 1000;

const stringKindMap: Record<string, ConversationChangeKind> = {
    conversationcreated: ConversationChangeKind.ConversationCreated,
    inboundmessageadded: ConversationChangeKind.InboundMessageAdded,
    outboundmessageadded: ConversationChangeKind.OutboundMessageAdded,
    messageupdated: ConversationChangeKind.MessageUpdated,
    messageremoved: ConversationChangeKind.MessageRemoved,
};

/** Coerces a numeric or string `kind` into the numeric enum, or null if unknown. */
export function normalizeConversationChangeKind(
    kind: ConversationChangeKind | number | string | null | undefined
): ConversationChangeKind | null {
    if (kind == null) return null;

    if (typeof kind === 'number') {
        return Object.values(ConversationChangeKind).includes(kind) ? kind : null;
    }

    const trimmed = kind.trim();
    // Numeric-as-string (e.g. "2") arriving from some serializers.
    if (/^\d+$/.test(trimmed)) {
        const numeric = Number(trimmed);
        return Object.values(ConversationChangeKind).includes(numeric) ? numeric : null;
    }

    return stringKindMap[trimmed.toLowerCase()] ?? null;
}

const isInboundMessage = (kind: ConversationChangeKind): boolean =>
    kind === ConversationChangeKind.InboundMessageAdded;

const isNewConversation = (kind: ConversationChangeKind): boolean =>
    kind === ConversationChangeKind.ConversationCreated;

interface ConversationUpdatesState {
    isConnected: boolean;
    lastEvent: NormalizedConversationChange | null;
}

const state = reactive<ConversationUpdatesState>({
    isConnected: false,
    lastEvent: null,
});

const listeners = new Set<ConversationChangeListener>();
const dedupeCache = new Map<string, number>();

const buildDedupeKey = (event: NormalizedConversationChange): string =>
    `${event.conversationSid}|${event.kind}|${event.lastActivityUtc}`;

// Evicts expired entries, then trims to the LRU cap (Map preserves insertion order).
const pruneDedupeCache = (now: number): void => {
    for (const [key, expiresAt] of dedupeCache) {
        if (expiresAt <= now) dedupeCache.delete(key);
    }
    while (dedupeCache.size > DEDUPE_MAX_ENTRIES) {
        const oldestKey = dedupeCache.keys().next().value;
        if (oldestKey === undefined) break;
        dedupeCache.delete(oldestKey);
    }
};

const isDuplicate = (event: NormalizedConversationChange): boolean => {
    const now = Date.now();
    pruneDedupeCache(now);
    const key = buildDedupeKey(event);
    if (dedupeCache.has(key)) return true;
    dedupeCache.set(key, now + DEDUPE_TTL_MS);
    return false;
};

const isOwnOutbound = (
    event: NormalizedConversationChange,
    currentUserId: string | null | undefined
): boolean => {
    if (!currentUserId) return false;
    if (!event.originatingUserId) return false;
    return event.originatingUserId === currentUserId;
};

const raiseToast = (event: NormalizedConversationChange): void => {
    // Privacy-safe text only: no body, author, or phone numbers.
    if (isNewConversation(event.kind)) {
        Notifications.Info('New conversation available');
        return;
    }
    if (isInboundMessage(event.kind)) {
        Notifications.Info('New message available');
    }
};

/**
 * Process a raw hub payload: normalize, dedupe, suppress own-outbound, emit a
 * toast for relevant kinds, and fan out to subscribed listeners (e.g. an open
 * `ConversationView`). `currentUserId` is the authenticated user's id used to
 * suppress the user's own outbound echoes.
 */
const handleConversationChanged = (
    payload: ConversationChangedDto | null | undefined,
    currentUserId: string | null | undefined
): void => {
    if (!payload || !payload.conversationSid) return;

    const kind = normalizeConversationChangeKind(payload.kind);
    if (kind == null) return;

    const event: NormalizedConversationChange = {
        ...payload,
        kind,
    };

    if (isDuplicate(event)) return;
    if (isOwnOutbound(event, currentUserId)) return;

    state.lastEvent = event;
    raiseToast(event);

    listeners.forEach((listener) => {
        try {
            listener(event);
        } catch (error) {
            console.error('Conversation update listener failed', error);
        }
    });
};

const setConnected = (connected: boolean): void => {
    state.isConnected = connected;
};

/** Subscribe to normalized conversation-change events. Returns an unsubscribe fn. */
const subscribe = (listener: ConversationChangeListener): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

// Test-only hook to reset the singleton between specs.
export const __resetConversationUpdates = (): void => {
    listeners.clear();
    dedupeCache.clear();
    state.isConnected = false;
    state.lastEvent = null;
};

/**
 * Singleton event bridge for real-time Twilio conversation changes. The global
 * SignalR plugin feeds hub payloads in via `handleConversationChanged`; views
 * subscribe to react in place.
 */
export function useConversationUpdates() {
    return {
        state: readonly(state),
        handleConversationChanged,
        setConnected,
        subscribe,
        normalizeConversationChangeKind,
    };
}
